import type {
  Staff,
  CharacterAppearance,
  ThemeData,
  TraitDefinition,
} from '../types';
import type { SeededRandom } from '../rng/SeededRandom';

let staffIdCounter = 0;

export function resetStaffIdCounter(): void {
  staffIdCounter = 0;
}

/** Set counter to max existing ID so new staff get unique IDs (call after load). */
export function syncStaffIdCounter(staffList: { id: string }[]): void {
  let max = 0;
  for (const s of staffList) {
    const n = parseInt(s.id.replace('staff_', ''), 10);
    if (n > max) max = n;
  }
  staffIdCounter = max;
}

function nextStaffId(): string {
  return `staff_${++staffIdCounter}`;
}

export function generateAppearance(rng: SeededRandom): CharacterAppearance {
  return {
    bodyType: rng.nextInt(0, 3),
    skinTone: rng.nextInt(0, 7),
    hairStyle: rng.nextInt(0, 15),
    hairColor: rng.nextInt(0, 9),
    outfit: rng.nextInt(0, 11),
    outfitColor: rng.nextInt(0, 11),
    accessory: rng.nextInt(0, 7),
  };
}

function generateName(rng: SeededRandom, themeData: ThemeData): string {
  const first = rng.pick(themeData.names.staffNames.firstNames);
  const last = rng.pick(themeData.names.staffNames.lastNames);
  return `${first} ${last}`;
}

function selectTraits(rng: SeededRandom, themeData: ThemeData): string[] {
  // Determine trait count: 30% 0, 40% 1, 20% 2, 10% 3
  const roll = rng.next();
  let traitCount: number;
  if (roll < 0.3) traitCount = 0;
  else if (roll < 0.7) traitCount = 1;
  else if (roll < 0.9) traitCount = 2;
  else traitCount = 3;

  if (traitCount === 0) return [];

  const traits: string[] = [];
  const available = [...themeData.entities.traits];

  for (let i = 0; i < traitCount && available.length > 0; i++) {
    // Select by rarity: 60% common, 30% uncommon, 10% rare
    const rarityRoll = rng.next();
    let targetRarity: TraitDefinition['rarity'];
    if (rarityRoll < 0.6) targetRarity = 'common';
    else if (rarityRoll < 0.9) targetRarity = 'uncommon';
    else targetRarity = 'rare';

    const candidates = available.filter((t) => {
      if (t.rarity !== targetRarity) return false;
      // Check for conflicts with already-selected traits
      for (const selected of traits) {
        if (t.conflicts.includes(selected)) return false;
        const selectedTrait = themeData.entities.traits.find((tt) => tt.id === selected);
        if (selectedTrait && selectedTrait.conflicts.includes(t.id)) return false;
      }
      // No duplicates
      if (traits.includes(t.id)) return false;
      return true;
    });

    if (candidates.length === 0) {
      // Fall back to any non-conflicting trait of any rarity
      const fallback = available.filter((t) => {
        for (const selected of traits) {
          if (t.conflicts.includes(selected)) return false;
          if (traits.includes(t.id)) return false;
        }
        return !traits.includes(t.id);
      });
      if (fallback.length === 0) break;
      const picked = rng.pick(fallback);
      traits.push(picked.id);
    } else {
      const picked = rng.pick(candidates);
      traits.push(picked.id);
    }
  }

  return traits;
}

function xpToNextLevel(level: number, themeData: ThemeData): number {
  return Math.floor(themeData.balance.xpBase * Math.pow(themeData.balance.xpGrowthRate, level));
}

/**
 * Generate a random staff member for the hiring pool.
 */
export function createStaff(rng: SeededRandom, themeData: ThemeData): Staff {
  const role = rng.pick(themeData.entities.staffRoles);
  const name = generateName(rng, themeData);
  const appearance = generateAppearance(rng);
  const traits = selectTraits(rng, themeData);

  // Randomize stats around the role's base (±30%)
  const stats: Record<string, number> = {};
  for (const [stat, base] of Object.entries(role.baseStats)) {
    const variance = rng.nextFloat(0.7, 1.3);
    stats[stat] = Math.round(base * variance);
  }

  // Salary varies ±20% from base
  const salary = Math.round(role.baseSalary * rng.nextFloat(0.8, 1.2));

  // Apply penny_pincher trait
  const hasPennyPincher = traits.includes('penny_pincher');
  const adjustedSalary = hasPennyPincher ? Math.round(salary * 0.85) : salary;

  return {
    id: nextStaffId(),
    name,
    role: role.id,
    level: 1,
    xp: 0,
    xpToNextLevel: xpToNextLevel(1, themeData),
    salary: adjustedSalary,
    desiredSalary: adjustedSalary,
    stats,
    traits,
    energy: 100,
    morale: 80,
    satisfaction: 70,
    teamId: null,
    yearsEmployed: 0,
    isOnNotice: false,
    noticeDaysRemaining: 0,
    demand: null,
    isOnLeave: false,
    leaveTicksRemaining: 0,
    isTraining: false,
    trainingTicksRemaining: 0,
    trainingTarget: null,
    equipmentBonus: 1.0,
    equipmentTicksRemaining: 0,
    lifetimeContribution: 0,
    isCEO: false,
    appearance,
    trainCooldownTicks: 0,
    seniorRole: null,
  };
}

/**
 * Create the player's CEO character with custom name, appearance, and bonus stats.
 */
export function createCEO(
  name: string,
  appearance: CharacterAppearance,
  bonusStats: Record<string, number>,
  themeData: ThemeData,
): Staff {
  const founder = themeData.entities.founderRole;

  const stats: Record<string, number> = {};
  for (const [stat, base] of Object.entries(founder.baseStats)) {
    stats[stat] = base + (bonusStats[stat] ?? 0);
  }

  return {
    id: nextStaffId(),
    name,
    role: founder.id,
    level: 1,
    xp: 0,
    xpToNextLevel: xpToNextLevel(1, themeData),
    salary: 0, // CEO doesn't draw a salary
    desiredSalary: 0,
    stats,
    traits: [],
    energy: 100,
    morale: 100,
    satisfaction: 100,
    teamId: null,
    yearsEmployed: 0,
    isOnNotice: false,
    noticeDaysRemaining: 0,
    demand: null,
    isOnLeave: false,
    leaveTicksRemaining: 0,
    isTraining: false,
    trainingTicksRemaining: 0,
    trainingTarget: null,
    equipmentBonus: 1.0,
    equipmentTicksRemaining: 0,
    lifetimeContribution: 0,
    isCEO: true,
    appearance,
    trainCooldownTicks: 0,
    seniorRole: null,
  };
}
